/**
 * NOS-129: Public media (sermon) detail at /media/:slug or /:orgSlug/media/:slug.
 */
import { useEffect, useMemo, useState } from "react"
import { Link } from "react-router-dom"
import { ArrowLeft, Loader2 } from "lucide-react"
import { PublicSitePage } from "@/components/PublicSitePage"
import { SEOHead } from "@/components/public/SEOHead"
import { getPublicSiteOrigin } from "@/lib/publicSiteUrl"
import { usePublicSiteShell } from "@/hooks/usePublicSiteShell"
import { usePublicMediaVideo } from "@/hooks/usePublicMedia"
import { getYouTubeEmbedSrc } from "@/lib/youtubeUtils"
import type { Organization, OrgCustomizations } from "@/templates/types"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"

interface MediaVideoPageProps {
  orgSlug: string
  videoSlug: string
  org?: Organization | null
  customizations?: OrgCustomizations | null
  basePath?: string
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return ""
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  })
}

export function MediaVideoPage({
  orgSlug,
  videoSlug,
  org: orgProp,
  customizations: custProp,
  basePath = "",
}: MediaVideoPageProps) {
  const shell = usePublicSiteShell(orgSlug, {
    org: orgProp,
    customizations: custProp,
    basePath,
  })
  const { org, orgName, primaryColor, loading: loadingOrg, notFound: siteNotFound } = shell

  const [playback, setPlayback] = useState<"video" | "audio">("video")

  const orgId = org?.id ?? null
  const { video, moreInSeries, loading, notFound, error } = usePublicMediaVideo(orgId, videoSlug || null)

  useEffect(() => {
    setPlayback("video")
  }, [videoSlug, video?.id])

  const mediaBase = `${basePath}/media`

  const canonicalUrl = useMemo(() => {
    if (!org || !videoSlug) return ""
    const origin = getPublicSiteOrigin(org)
    const path = basePath ? `${basePath}/media/${videoSlug}` : `/media/${videoSlug}`
    return `${origin}${path}`
  }, [org, basePath, videoSlug])

  const description = useMemo(() => {
    const d = video?.description?.trim()
    if (d) return d.length <= 160 ? d : `${d.slice(0, 159)}…`
    const m = org?.mission_statement?.trim()
    if (m) return m.length <= 160 ? m : `${m.slice(0, 159)}…`
    return `${orgName} — ${video?.title ?? "Media"}`
  }, [video, org, orgName])

  const ogImage = video?.thumbnail_url || org?.logo_url || null

  const thumbUrl = (youtubeId: string | null, thumb: string | null) =>
    thumb || (youtubeId ? `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg` : "")

  const embedSrc = video?.youtube_video_id ? getYouTubeEmbedSrc(video.youtube_video_id) : ""

  const showHead = org && !loadingOrg && !siteNotFound && video && !notFound
  const show404 = org && !loadingOrg && !loading && (notFound || !video)

  if (!orgSlug || !videoSlug) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p className="text-muted-foreground">Not found.</p>
      </div>
    )
  }

  if (siteNotFound || (!org && !loadingOrg)) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p className="text-muted-foreground">Organization not found.</p>
      </div>
    )
  }

  if (show404) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 px-4">
        <p className="text-muted-foreground">This message is not available.</p>
        <Button variant="outline" asChild>
          <Link to={mediaBase}>Back to media</Link>
        </Button>
      </div>
    )
  }

  return (
    <PublicSitePage
      shell={shell}
      head={
        showHead && canonicalUrl ? (
          <SEOHead
            title={`${video.title} — ${orgName}`}
            description={description}
            canonicalUrl={canonicalUrl}
            ogType="article"
            imageUrl={ogImage}
          />
        ) : null
      }
    >
      <main className="mx-auto max-w-4xl px-4 py-10 sm:px-6">
        {error && (
          <div className="mb-6 rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">{error}</div>
        )}

        {loading || loadingOrg || !video ? (
          <div className="flex justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            <div className="mb-6">
              <Button variant="ghost" size="sm" asChild className="-ml-2 mb-4">
                <Link to={mediaBase} className="inline-flex items-center gap-1 text-muted-foreground">
                  <ArrowLeft className="h-4 w-4" />
                  Media library
                </Link>
              </Button>
              <h1 className="text-3xl font-bold" style={{ color: primaryColor }}>
                {video.title}
              </h1>
              <p className="mt-2 text-sm text-muted-foreground">{formatDate(video.published_at)}</p>
              {video.scripture_passage?.trim() ? (
                <p className="mt-3 text-sm font-medium">{video.scripture_passage}</p>
              ) : null}

              <div className="mt-4 flex flex-wrap gap-2">
                {video.media_series ? (
                  <Link to={`${mediaBase}?series=${encodeURIComponent(video.media_series.slug)}`}>
                    <Badge variant="secondary" className="cursor-pointer hover:bg-secondary/80">
                      Series: {video.media_series.title}
                    </Badge>
                  </Link>
                ) : null}
                {(video.media_video_speakers ?? []).map((r) =>
                  r.media_speakers ? (
                    <Link key={r.speaker_id} to={`${mediaBase}?speaker=${encodeURIComponent(r.speaker_id)}`}>
                      <Badge variant="outline" className="cursor-pointer">
                        {r.media_speakers.name}
                      </Badge>
                    </Link>
                  ) : null
                )}
                {(video.media_video_topics ?? []).map((r) =>
                  r.media_topics ? (
                    <Link key={r.topic_id} to={`${mediaBase}?topic=${encodeURIComponent(r.media_topics.slug)}`}>
                      <Badge variant="outline" className="cursor-pointer">
                        {r.media_topics.name}
                      </Badge>
                    </Link>
                  ) : null
                )}
              </div>
            </div>

            {video.audio_url?.trim() ? (
              <div className="mb-4 flex flex-wrap gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant={playback === "video" ? "default" : "outline"}
                  onClick={() => setPlayback("video")}
                >
                  Video
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={playback === "audio" ? "default" : "outline"}
                  onClick={() => setPlayback("audio")}
                  data-testid="media-audio-toggle"
                >
                  Audio only
                </Button>
              </div>
            ) : null}

            <div className="relative mb-6 aspect-video w-full overflow-hidden rounded-lg bg-black">
              {playback === "video" && embedSrc ? (
                <iframe
                  title={video.title}
                  src={`${embedSrc}?rel=0`}
                  className="absolute inset-0 h-full w-full border-0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  referrerPolicy="strict-origin-when-cross-origin"
                />
              ) : null}
              {playback === "audio" && video.audio_url?.trim() ? (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-muted px-4">
                  <audio controls className="w-full max-w-md" src={video.audio_url.trim()} data-testid="media-audio-player">
                    <track kind="captions" />
                  </audio>
                </div>
              ) : null}
            </div>

            {playback === "video" && embedSrc ? (
              <p className="mb-8 text-xs text-muted-foreground">
                Video plays from YouTube. Playback may include ads or suggested content determined by YouTube, not{" "}
                {orgName}.
              </p>
            ) : null}

            {video.description?.trim() ? (
              <div className="prose prose-neutral mb-10 max-w-none dark:prose-invert">
                <p className="whitespace-pre-wrap text-sm leading-relaxed">{video.description}</p>
              </div>
            ) : null}

            {video.transcript?.trim() ? (
              <details className="mb-6 rounded-lg border bg-card p-4">
                <summary className="cursor-pointer font-medium">Transcript</summary>
                <div className="mt-3 max-h-[24rem] overflow-y-auto whitespace-pre-wrap text-sm text-muted-foreground">
                  {video.transcript}
                </div>
              </details>
            ) : null}

            {video.ai_study_guide?.trim() ? (
              <details className="mb-10 rounded-lg border bg-card p-4">
                <summary className="cursor-pointer font-medium">Study guide</summary>
                <pre className="mt-3 max-h-[24rem] overflow-y-auto whitespace-pre-wrap font-sans text-sm text-muted-foreground">
                  {video.ai_study_guide}
                </pre>
              </details>
            ) : null}

            {moreInSeries.length > 0 && video.media_series ? (
              <section className="border-t pt-10">
                <h2 className="mb-4 text-lg font-semibold">More in {video.media_series.title}</h2>
                <div className="grid gap-4 sm:grid-cols-2">
                  {moreInSeries.map((v) => (
                    <Link key={v.id} to={`${basePath}/media/${v.slug}`}>
                      <Card className="h-full overflow-hidden transition-shadow hover:shadow-md">
                        <div className="flex gap-3">
                          <div className="w-28 shrink-0 overflow-hidden bg-muted">
                            {thumbUrl(v.youtube_video_id, v.thumbnail_url) ? (
                              <img
                                src={thumbUrl(v.youtube_video_id, v.thumbnail_url)}
                                alt=""
                                className="h-full w-full object-cover"
                              />
                            ) : null}
                          </div>
                          <CardContent className="flex-1 p-3">
                            <p className="line-clamp-2 font-medium leading-snug">{v.title}</p>
                            <p className="mt-1 text-xs text-muted-foreground">{formatDate(v.published_at)}</p>
                          </CardContent>
                        </div>
                      </Card>
                    </Link>
                  ))}
                </div>
              </section>
            ) : null}
          </>
        )}
      </main>
    </PublicSitePage>
  )
}
